import React from 'react';
import { formatMonthName } from '../../utils/dateFormatter';

/**
 * OwnerMonthlyTrend — bar chart of revenue per month (owners care about income,
 * not booking volume; the booking count is kept as a secondary subtitle + tooltip).
 *
 * Brand palette v2.1: revenue bars purple #471396 (matches the revenue KPI card —
 * "purple = money"); upcoming month orange #ff9f53.
 *
 * Props:
 *   monthlyTrend: Array<{ month: string, bookings: number, revenue: number, isUpcoming?: boolean }>
 */

const cardShell = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: 'clamp(16px, 5vw, 28px)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  border: '1px solid #e8e8e8'
};

const heading = { margin: '0 0 20px 0', color: '#333', fontSize: 'clamp(16px, 3.5vw, 18px)', fontWeight: '600' };

// Compact label that fits inside a bar (e.g. 12400 → "12.4k", 950 → "950")
const compactRevenue = (value) => {
  if (value >= 1000) {
    const k = value / 1000;
    return `${Number(k.toFixed(1))}k`;
  }
  return `${Math.round(value)}`;
};

const exactRevenue = (value) => value.toLocaleString(undefined, { maximumFractionDigits: 0 });

const OwnerMonthlyTrend = ({ monthlyTrend }) => {
  if (!monthlyTrend || monthlyTrend.length === 0) return null;

  const maxRevenue = Math.max(...monthlyTrend.map(m => m.revenue || 0), 1);
  const hasRevenue = monthlyTrend.some(m => (m.revenue || 0) > 0);

  return (
    <div dir="rtl" style={cardShell}>
      <h4 style={heading}>الإيرادات بالشهر</h4>

      {!hasRevenue ? (
        <div style={{ textAlign: 'center', padding: 'clamp(24px, 6vw, 40px) 16px', color: '#888' }}>
          <i className="bi bi-bar-chart-line" style={{ fontSize: '32px', color: '#c9c5d6' }}></i>
          <p style={{ margin: '12px 0 0', fontSize: '15px', fontWeight: '500', color: '#666' }}>
            لا توجد إيرادات في هذه الفترة
          </p>
        </div>
      ) : (
        <div className="touch-scroll-x" style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 'clamp(8px, 2vw, 12px)',
          height: 'clamp(170px, 34vw, 185px)'
        }}>
          {monthlyTrend.map((monthData, index) => {
            const revenue = monthData.revenue || 0;
            const bookings = monthData.bookings || 0;
            const height = (revenue / maxRevenue) * 100;
            const color = monthData.isUpcoming ? '#ff9f53' : '#471396';
            const monthLabel = formatMonthName(monthData.month) || monthData.month || '-';
            const tooltip = `${exactRevenue(revenue)} ر.س · ${bookings} حجز`;

            return (
              <div key={index} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: '1 0 auto',
                minWidth: '40px',
                gap: '6px'
              }} title={tooltip}>
                {/* Value label ABOVE the bar — always visible regardless of bar height */}
                <span style={{
                  color: '#090040',
                  fontSize: 'clamp(11px, 2.3vw, 13px)',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  minHeight: '16px'
                }}>
                  {revenue > 0 ? compactRevenue(revenue) : ''}
                </span>
                <div style={{
                  width: '100%',
                  maxWidth: 'clamp(40px, 9vw, 56px)',
                  height: `${Math.max(height, 8)}px`,
                  backgroundColor: color,
                  border: monthData.isUpcoming
                    ? '1px solid rgba(255, 159, 83, 0.4)'
                    : '1px solid rgba(71, 19, 150, 0.2)',
                  borderRadius: '8px 8px 0 0',
                  transition: 'height 0.3s ease'
                }}></div>
                <span style={{
                  color: '#444',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  direction: 'rtl'
                }}>
                  {monthLabel}
                </span>
                <span style={{
                  color: '#999',
                  fontSize: 'clamp(10px, 2vw, 11px)',
                  whiteSpace: 'nowrap'
                }}>
                  {bookings} حجز
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OwnerMonthlyTrend;
